/**
 * Inline status pill — sharp rectangle, uppercase label, coloured dot prefix.
 * Used across all admin pages for booking states, vehicle states, payment states, etc.
 */

type PillTone = 'ok' | 'warn' | 'err' | 'brand' | 'neutral' | 'amber';

const TONE_STYLES: Record<PillTone, React.CSSProperties> = {
  ok: {
    background: '#e6f2ec',
    color: 'var(--lv-ok)',
    borderColor: '#c4ddd0',
  },
  warn: {
    background: '#fef3e2',
    color: 'var(--lv-warn)',
    borderColor: '#fde68a',
  },
  err: {
    background: '#fce4e4',
    color: 'var(--lv-err)',
    borderColor: '#fca5a5',
  },
  brand: {
    background: '#eaeeed',
    color: 'var(--lv-brand)',
    borderColor: '#c8d8d7',
  },
  neutral: {
    background: 'var(--lv-surface-soft)',
    color: 'var(--lv-gray-500)',
    borderColor: 'var(--lv-line)',
  },
  amber: {
    background: '#fef3e2',
    color: '#92400e',
    borderColor: '#fde68a',
  },
};

export function Pill({ label, tone = 'neutral' }: { label: string; tone?: PillTone }) {
  const s = TONE_STYLES[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 8px',
        border: `1px solid ${s.borderColor as string}`,
        background: s.background,
        color: s.color,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: s.color,
          flexShrink: 0,
        }}
      />
      {label}
    </span>
  );
}

/** Map a booking/vehicle/payment state string to a PillTone */
export function stateToTone(state: string): PillTone {
  switch (state) {
    case 'active':
    case 'confirmed':
    case 'approved':
    case 'succeeded':
      return 'ok';
    case 'in_progress':
    case 'reserved_pending_payment':
      return 'brand';
    case 'maintenance':
    case 'pending':
    case 'refunded':
      return 'warn';
    case 'cancelled':
    case 'failed':
    case 'rejected':
    case 'retired':
      return 'err';
    case 'completed':
      return 'neutral';
    case 'overdue':
      return 'err';
    case 'draft':
      return 'neutral';
    default:
      return 'neutral';
  }
}

/** Human-readable PT-PT labels for known states */
export function stateLabel(state: string): string {
  const MAP: Record<string, string> = {
    active: 'ACTIVA',
    draft: 'RASCUNHO',
    maintenance: 'MANUT.',
    retired: 'RETIRADA',
    confirmed: 'CONFIRMADA',
    in_progress: 'EM CURSO',
    completed: 'CONCLUÍDA',
    cancelled: 'CANCELADA',
    reserved_pending_payment: 'AGUARDA PGT.',
    overdue: 'EM ATRASO',
    pending: 'PENDENTE',
    approved: 'APROVADA',
    rejected: 'REJEITADA',
    succeeded: 'PAGO',
    failed: 'FALHOU',
    refunded: 'REEMBOLSO',
  };
  return MAP[state] ?? state.toUpperCase();
}
